import type { StaffAnalyticsAccess, StaffAnalyticsEventDetail } from "@lafayette/schemas";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { deepLinks } from "../../navigation/deep-links";
import { useAreaHref } from "../../navigation/use-offering";
import { AsyncState } from "../../ui/async-state";
import { Button } from "../../ui/button";
import { cn } from "../../ui/cn";
import { Icon } from "../../ui/icon";
import { eventStatusOf, firstNameOf, plainWords } from "../event-sentences";
import { type CustomerView, isRefusal } from "../queries";
import { eventTime, ownProperties, valueText, visitSpan } from "./events-model";
import { useEventDetail, useVisitSteps } from "./events-queries";
import { StatusPill } from "./StatusPill";
import { VisitTimeline } from "./VisitTimeline";

// The detail panel of one event (Paper "Event detail panel"): the name and the status, the time to the second, the device and the app version, the
// event in plain words, every property, the visit it belongs to ("today #1 · step 5 of 7") with the visit step by step, and the two buttons.
// The consent line says only that consent was checked before the event was sent (ADR 0017, Q7): it has NO time, because the checked time of an
// event is not stored. A refusal says the same words as every other refusal of the area.

const SECTION = "text-xs font-semibold tracking-[0.05em] text-muted-foreground uppercase";

function PropertyRow({
  name,
  value,
  href,
}: Readonly<{ name: string; value: string; href?: string | null }>) {
  return (
    <div className="flex justify-between gap-3 font-mono text-xs leading-[18px]">
      <dt className="text-muted-foreground">{name}</dt>
      <dd className="m-0 text-right [overflow-wrap:anywhere]">
        {href === undefined || href === null ? (
          value
        ) : (
          <Link to={href} className="font-semibold text-link underline">
            {value}
          </Link>
        )}
      </dd>
    </div>
  );
}

/**
 * "Today 09:12 · step 5 of 7": when the visit started and where the event sits in it. The mock-up says "today #1" (the number of the visit that
 * day), but no source gives that number without reading every visit of the day, so it is not drawn.
 */
function visitLine(detail: StaffAnalyticsEventDetail, tz: string, now: string): string | null {
  return detail.visit === null
    ? null
    : `${eventTime(detail.visit.startedAt, tz, now)} · step ${detail.visit.stepIndex} of ${detail.visit.stepCount}`;
}

type Event = NonNullable<StaffAnalyticsEventDetail["event"]>;

/** Loading, failed, refused or empty: what stands in the place of the panel. */
function PanelNotice({
  detail,
  className,
}: Readonly<{ detail: ReturnType<typeof useEventDetail>; className?: string | undefined }>) {
  let body: ReactNode;
  if (detail.isError) {
    const refused = isRefusal(detail.error);
    body = (
      <AsyncState
        kind="error"
        title={refused ? "This event is not available to you" : "The event could not be loaded"}
        message={
          refused
            ? "You may open only the events of the customers you are allowed to see."
            : "Nothing was changed. Try again in a moment."
        }
        {...(refused ? {} : { onRetry: () => detail.refetch() })}
      />
    );
  } else if (detail.data === undefined) {
    body = <AsyncState kind="loading" title="Loading the event" message="One moment." />;
  } else {
    body = (
      <AsyncState
        kind="empty"
        title="No event to show"
        message="Events are not shown for this customer right now."
      />
    );
  }
  return <div className={className}>{body}</div>;
}

function PanelHeader({
  event,
  where,
  onClose,
  showClose,
}: Readonly<{ event: Event; where: string; onClose: () => void; showClose: boolean }>) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="grid min-w-0 gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2
            id="event-detail-title"
            className="m-0 font-mono text-[15px] leading-5 font-bold [overflow-wrap:anywhere]"
          >
            {event.event}
          </h2>
          <StatusPill status={eventStatusOf(event.event)} />
        </div>
        <p className="m-0 text-xs leading-4 text-muted-foreground">{where}</p>
      </div>
      {showClose ? (
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-[30px]"
          onClick={onClose}
        >
          <Icon name="cancel-01" size="sm" />
          <span className="sr-only">Close the event</span>
        </Button>
      ) : null}
    </div>
  );
}

/** Every property of the event (its own, then the five context keys), the visit line and the consent line. */
function PropertyList({
  event,
  requestLink,
  visitText,
}: Readonly<{ event: Event; requestLink: string | null; visitText: string | null }>) {
  const own = ownProperties(event.properties);
  const context = Object.entries(event.properties).filter(
    ([key]) => !own.some(([ownKey]) => ownKey === key),
  );
  return (
    <div className="grid gap-1.5">
      <h3 className={cn(SECTION, "m-0")}>Properties</h3>
      <dl className="m-0 grid gap-1.5">
        {own.map(([key, value]) => (
          <PropertyRow
            key={key}
            name={key}
            value={valueText(value)}
            href={key === "request_id" ? requestLink : null}
          />
        ))}
        {context.map(([key, value]) => (
          <PropertyRow key={key} name={key} value={valueText(value)} />
        ))}
        {visitText === null ? null : <PropertyRow name="visit" value={visitText} />}
        <div className="flex justify-between gap-3 font-mono text-xs leading-[18px]">
          <dt className="text-muted-foreground">consent</dt>
          <dd className="m-0 text-right text-success">checked before it was sent</dd>
        </div>
      </dl>
    </div>
  );
}

function VisitSection({
  steps,
  failed,
  openUuid,
  tz,
  now,
}: Readonly<{
  steps: ReturnType<typeof useVisitSteps>;
  failed: boolean;
  openUuid: string;
  tz: string;
  now: string;
}>) {
  const list = steps.data?.steps ?? [];
  if (failed) {
    return (
      <p className="m-0 text-xs text-muted-foreground" role="status">
        The steps of this visit could not be loaded.
      </p>
    );
  }
  if (steps.data === undefined || list.length === 0) return null;
  const { visit } = steps.data;
  return (
    <div className="grid gap-2 border-t border-hairline pt-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className={cn(SECTION, "m-0")}>This visit, step by step</h3>
        {visit === null ? null : (
          <span className="text-xs text-muted-foreground">
            {visitSpan(visit.startedAt, visit.endedAt, tz, now)}
          </span>
        )}
      </div>
      <VisitTimeline steps={list} openUuid={openUuid} tz={tz} />
      {steps.data.stepsTruncated ? (
        <p className="m-0 text-xs text-muted-foreground">Only the first 100 steps are shown.</p>
      ) : null}
    </div>
  );
}

function PanelActions({
  event,
  requestLink,
  visitId,
  onShowOnly,
  onShowVisit,
}: Readonly<{
  event: Event;
  requestLink: string | null;
  visitId: string | null;
  onShowOnly: (event: string) => void;
  onShowVisit: (visitId: string) => void;
}>) {
  return (
    <div className="flex flex-wrap gap-2">
      {requestLink === null ? null : (
        <Button asChild className="grow rounded-xl">
          <Link to={requestLink}>Open the request</Link>
        </Button>
      )}
      <Button
        type="button"
        variant="outline"
        className="grow rounded-xl"
        onClick={() => onShowOnly(event.event)}
      >
        Show only this event
      </Button>
      {visitId === null ? null : (
        <Button
          type="button"
          variant="outline"
          className="grow rounded-xl"
          onClick={() => onShowVisit(visitId)}
        >
          Show this visit only
        </Button>
      )}
    </div>
  );
}

const platformWords = { ios: "iOS", android: "Android" } as const;

/** The device line of the panel: the time to the second, the platform and the app version, where known. */
function whereLine(event: Event, tz: string, now: string): string {
  return [
    eventTime(event.timestamp, tz, now, true),
    event.platform === null ? null : platformWords[event.platform],
    event.appVersion === null ? null : `app ${event.appVersion}`,
  ]
    .filter((part) => part !== null)
    .join(" · ");
}

export function EventDetailPanel({
  view,
  customerId,
  eventUuid,
  access,
  onClose,
  onShowOnly,
  onShowVisit,
  showClose = true,
  className,
}: Readonly<{
  view: CustomerView;
  customerId: string;
  eventUuid: string;
  access: StaffAnalyticsAccess;
  onClose: () => void;
  onShowOnly: (event: string) => void;
  onShowVisit: (visitId: string) => void;
  /** `false` inside the sheet of a narrow screen, which has its own close button. */
  showClose?: boolean;
  className?: string;
}>) {
  const detail = useEventDetail(view, customerId, eventUuid);
  const visitId = detail.data?.visit?.visitId ?? null;
  const steps = useVisitSteps(view, customerId, visitId);
  const areaHref = useAreaHref();
  const event = detail.data?.event ?? null;
  if (detail.data === undefined || event === null) {
    return <PanelNotice detail={detail} className={className} />;
  }
  const { tz } = access.window;
  const now = access.window.to;
  const requestId =
    typeof event.properties.request_id === "string" ? event.properties.request_id : null;
  const requestLink =
    requestId === null ? null : areaHref("requests", deepLinks.request(requestId));
  return (
    <section
      aria-labelledby="event-detail-title"
      className={cn(
        "flex flex-col gap-3.5 rounded-2xl border border-hairline bg-card px-5 py-[18px] shadow-lg",
        className,
      )}
    >
      <PanelHeader
        event={event}
        where={whereLine(event, tz, now)}
        onClose={onClose}
        showClose={showClose}
      />
      <p className="m-0 rounded-[10px] bg-muted px-3 py-2.5 text-[13px] leading-[19px]">
        {plainWords(event.event, event.properties, firstNameOf(access.displayName))}
      </p>
      <PropertyList
        event={event}
        requestLink={requestLink}
        visitText={visitLine(detail.data, tz, now)}
      />
      <VisitSection steps={steps} failed={steps.isError} openUuid={event.uuid} tz={tz} now={now} />
      <PanelActions
        event={event}
        requestLink={requestLink}
        visitId={visitId}
        onShowOnly={onShowOnly}
        onShowVisit={onShowVisit}
      />
    </section>
  );
}
